import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Users, FileText, ListTodo, ArrowRight, BookOpen, Clock, TrendingUp } from "lucide-react";
import { customersApi, serviceReportsApi, knowledgeApi } from "@/lib/api";
import api from "@/lib/api";
import { navItems, internItems } from "./navConfig";

const NAV_ENTRIES = [...navItems, ...internItems];

const RECENT_KEY = "crm-recent-searches";
const FREQ_KEY = "crm-search-freq";
const readJSON = (k, def) => { try { return JSON.parse(localStorage.getItem(k)) ?? def; } catch { return def; } };
const getRecent = () => (Array.isArray(readJSON(RECENT_KEY, [])) ? readJSON(RECENT_KEY, []) : []);
const getFrequent = () => {
  const map = readJSON(FREQ_KEY, {}) || {};
  return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([t]) => t);
};
const recordSearch = (term) => {
  const t = (term || "").trim();
  if (!t || t.length < 2) return;
  const recent = [t, ...getRecent().filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, 6);
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(recent)); } catch (e) { console.debug(e); }
  const map = readJSON(FREQ_KEY, {}) || {};
  map[t] = (map[t] || 0) + 1;
  try { localStorage.setItem(FREQ_KEY, JSON.stringify(map)); } catch (e) { console.debug(e); }
};

// Globale Befehls-Palette (nur PC-Ansicht) — Strg/⌘+K zum schnellen Springen
export function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [reports, setReports] = useState([]);
  const [kbArticles, setKbArticles] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState("");
  const [recent, setRecent] = useState([]);
  const [frequent, setFrequent] = useState([]);

  useEffect(() => {
    if (open) { setRecent(getRecent()); setFrequent(getFrequent()); }
  }, [open]);

  useEffect(() => {
    const isDesktop = () => window.matchMedia("(min-width: 1024px)").matches;
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        if (!isDesktop()) return; // nur PC-Ansicht
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    const onOpenEvent = () => setOpen(true);
    window.addEventListener("open-command-palette", onOpenEvent);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("open-command-palette", onOpenEvent);
    };
  }, []);

  useEffect(() => {
    if (!open || loaded) return;
    (async () => {
      try {
        const [c, t, r] = await Promise.all([
          customersApi.getAllLite().catch((e) => { console.error("Kunden laden fehlgeschlagen:", e); return { data: [] }; }),
          api.get("/tasks").catch((e) => { console.error("Aufgaben laden fehlgeschlagen:", e); return { data: [] }; }),
          serviceReportsApi.getAll().catch((e) => { console.error("Serviceberichte laden fehlgeschlagen:", e); return { data: [] }; }),
        ]);
        setCustomers(Array.isArray(c.data) ? c.data : []);
        setTasks(Array.isArray(t.data) ? t.data : []);
        setReports(Array.isArray(r.data) ? r.data : []);
        try {
          const k = await knowledgeApi.getArticles();
          setKbArticles(Array.isArray(k.data) ? k.data : []);
        } catch (e) { console.error("Wissensartikel laden fehlgeschlagen:", e); }
        setLoaded(true);
      } catch (e) {
        console.error("Command-Palette Daten konnten nicht geladen werden:", e);
      }
    })();
  }, [open, loaded]);

  const go = useCallback((path) => {
    recordSearch(query);
    setOpen(false);
    setQuery("");
    navigate(path);
  }, [navigate, query]);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <DialogTitle className="sr-only">Befehls-Palette</DialogTitle>
      <DialogDescription className="sr-only">Schnell zu Kunden, Berichten und Aufgaben springen</DialogDescription>
      <CommandInput value={query} onValueChange={setQuery} placeholder="Springe zu Kunden, Berichten, Aufgaben… (Strg/⌘+K)" data-testid="command-palette-input" />
      <CommandList>
        <CommandEmpty>Keine Treffer gefunden.</CommandEmpty>

        {!query.trim() && recent.length > 0 && (
          <CommandGroup heading="Zuletzt gesucht">
            {recent.map((t) => (
              <CommandItem key={`recent-${t}`} value={`recent ${t}`} onSelect={() => setQuery(t)} data-testid={`command-recent-${t}`}>
                <Clock className="text-muted-foreground" />
                <span className="truncate">{t}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {!query.trim() && frequent.length > 0 && (
          <CommandGroup heading="Häufig gesucht">
            {frequent.map((t) => (
              <CommandItem key={`freq-${t}`} value={`freq ${t}`} onSelect={() => setQuery(t)} data-testid={`command-frequent-${t}`}>
                <TrendingUp className="text-muted-foreground" />
                <span className="truncate">{t}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandGroup heading="Navigation">
          {NAV_ENTRIES.map((item) => {
            const Icon = item.icon;
            return (
              <CommandItem
                key={`nav-${item.path}`}
                value={`nav ${item.label}`}
                onSelect={() => go(item.path)}
                data-testid={`command-nav-${item.path.replace("/", "") || "dashboard"}`}
              >
                <Icon className="text-muted-foreground" />
                <span>{item.label}</span>
                <ArrowRight className="ml-auto w-3.5 h-3.5 opacity-40" />
              </CommandItem>
            );
          })}
        </CommandGroup>

        {customers.length > 0 && (
          <CommandGroup heading="Kunden">
            {customers.map((c) => (
              <CommandItem
                key={`cust-${c.id}`}
                value={`kunde ${c.company_name} ${c.city || ""} ${c.postal_code || ""}`}
                onSelect={() => go(`/customers/${c.id}`)}
                data-testid={`command-customer-${c.id}`}
              >
                <Users className="text-muted-foreground" />
                <span className="truncate">{c.company_name}</span>
                {c.city && <span className="text-xs text-muted-foreground ml-1 truncate">· {c.city}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {tasks.length > 0 && (
          <CommandGroup heading="Aufgaben">
            {tasks.map((t) => (
              <CommandItem
                key={`task-${t.id}`}
                value={`aufgabe ${t.title}`}
                onSelect={() => go("/tasks")}
                data-testid={`command-task-${t.id}`}
              >
                <ListTodo className="text-muted-foreground" />
                <span className="truncate">{t.title}</span>
                {t.status && <span className="text-xs text-muted-foreground ml-auto shrink-0">{t.status}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {reports.length > 0 && (
          <CommandGroup heading="Serviceberichte">
            {reports.map((r) => (
              <CommandItem
                key={`report-${r.id}`}
                value={`bericht ${r.customer_name || ""} ${r.title || ""} ${r.service_date || ""}`}
                onSelect={() => go(`/service-reports/${r.id}/edit`)}
                data-testid={`command-report-${r.id}`}
              >
                <FileText className="text-muted-foreground" />
                <span className="truncate">
                  {r.title || "Bericht"}{r.customer_name ? ` · ${r.customer_name}` : ""}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {kbArticles.length > 0 && (
          <CommandGroup heading="Wissensdatenbank">
            {kbArticles.map((a) => (
              <CommandItem
                key={`kb-${a.id}`}
                value={`wissen ${a.title} ${a.category || ""} ${(a.plain_text || "").slice(0, 200)}`}
                onSelect={() => go(`/knowledge?article=${a.id}`)}
                data-testid={`command-kb-${a.id}`}
              >
                <BookOpen className="text-muted-foreground" />
                <span className="truncate">{a.title}</span>
                {a.category && <span className="text-xs text-muted-foreground ml-auto shrink-0 truncate max-w-[40%]">{a.category}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
